/**
 * Reusable business card component.
 * Prop-driven so the same layout powers future NGO/partner promo cards.
 *
 * Dimensions: US-standard 3.5" x 2" @ 300 DPI = 1050 x 600 px.
 */
import React, { forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const CARD_W_PX = 1050;   // 3.5" @ 300 DPI
const CARD_H_PX = 600;    // 2.0" @ 300 DPI
const PAD_PX = 60;        // ~0.2" safe zone inside 0.125" bleed
const LOGO_PX = 110;      // matches pet photo size — see props

// Minimal X (Twitter) glyph — inline so PNG export doesn't rely on external font
const XGlyph = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path
      fill="currentColor"
      d="M18.244 2H21l-6.51 7.44L22 22h-6.828l-4.79-6.26L4.8 22H2.04l6.98-7.98L2 2h6.914l4.32 5.71L18.244 2Zm-2.4 18h1.66L7.24 4H5.48l10.364 16Z"
    />
  </svg>
);

const TikTokGlyph = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path
      fill="currentColor"
      d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.79A8.16 8.16 0 0 0 21 10.35V6.94a4.85 4.85 0 0 1-1.41-.25Z"
    />
  </svg>
);

const SocialLine = ({ platform, handle }) => {
  if (!handle) return null;
  const Glyph = platform === 'tiktok' ? TikTokGlyph : XGlyph;
  return (
    <div className="flex items-center gap-2 justify-end text-white/90" style={{ fontSize: '17px' }}>
      <Glyph size={18} />
      <span className="select-all">{handle.startsWith('@') ? handle : `@${handle}`}</span>
    </div>
  );
};

export const BusinessCardFront = forwardRef(function BusinessCardFront(
  {
    fullName,
    title,
    email,
    phone,
    website,
    tagline = 'Member-governed community for dog people',
    orgName = 'American Dog Club',
    logoSrc = '/branding/princess/princess-mark-180.png',
    petPhotoSrc = '/branding/princess/princess-portrait-256.png',
    petName,               // caption/alt text
    xHandle,               // optional
    tiktokHandle,          // optional
    accent = 'amber',      // reserved for future NGO cards
    qrTarget,
  },
  ref
) {
  const qrValue =
    qrTarget ||
    (website?.startsWith('http') ? website : `https://${website || 'americandogclub.com'}`);

  // Empty string means "no user pick" — fall back to the default Princess
  // portrait so the pet slot always has something warm to show.
  const effectivePetSrc = petPhotoSrc || '/branding/princess/princess-portrait-256.png';
  const hasPet = true; // pet slot is always shown (defaults to Princess)
  const hasSocials = !!(xHandle || tiktokHandle);

  return (
    <div
      ref={ref}
      style={{
        width: `${CARD_W_PX}px`,
        height: `${CARD_H_PX}px`,
        transform: 'scale(0.5)',
        transformOrigin: 'top left',
      }}
      className="relative overflow-hidden shadow-2xl"
      data-testid="business-card-front"
    >
      {/* Base fill — chocolate brown */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#5c2a0d] via-[#78350f] to-[#4a1f08]" />

      {/* Paw-print watermark */}
      <svg
        className="absolute inset-0 opacity-[0.06]"
        width="100%"
        height="100%"
        viewBox="0 0 1050 600"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <defs>
          <pattern id="paws" x="0" y="0" width="140" height="140" patternUnits="userSpaceOnUse">
            <g fill="#fbbf24">
              <circle cx="70" cy="82" r="18" />
              <ellipse cx="46" cy="58" rx="10" ry="14" />
              <ellipse cx="94" cy="58" rx="10" ry="14" />
              <ellipse cx="30" cy="82" rx="8" ry="12" />
              <ellipse cx="110" cy="82" rx="8" ry="12" />
            </g>
          </pattern>
        </defs>
        <rect width="1050" height="600" fill="url(#paws)" />
      </svg>

      {/* Amber accent bar on the left edge */}
      <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-b from-amber-400 via-amber-500 to-amber-600" />

      {/* Content */}
      <div
        className="absolute inset-0 flex"
        style={{ padding: `${PAD_PX}px ${PAD_PX}px ${PAD_PX}px ${PAD_PX + 32}px` }}
      >
        {/* LEFT column — logo + org + tagline */}
        <div className="flex flex-col justify-between items-start" style={{ width: '420px' }}>
          <div className="flex items-center gap-4">
            <div
              className="rounded-full overflow-hidden bg-amber-100 ring-4 ring-amber-400/60 shrink-0"
              style={{ width: `${LOGO_PX}px`, height: `${LOGO_PX}px` }}
            >
              <img
                src={logoSrc}
                alt={`${orgName} logo`}
                crossOrigin="anonymous"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-white">
              <div
                style={{ fontSize: '18px' }}
                className="uppercase tracking-[0.3em] text-amber-300/90 font-medium"
              >
                {orgName.split(' ')[0]}
              </div>
              <div
                style={{ fontSize: '38px', lineHeight: 1 }}
                className="font-serif italic text-amber-300"
              >
                {orgName.split(' ').slice(1).join(' ') || 'Club'}
              </div>
            </div>
          </div>

          <div
            className="text-white"
            style={{ fontSize: '24px', lineHeight: 1.25, maxWidth: '420px' }}
          >
            <div className="font-serif italic text-amber-200/95">&ldquo;{tagline}&rdquo;</div>
          </div>
        </div>

        {/* RIGHT column — pet photo + name at top, contact + QR at bottom */}
        <div className="flex-1 flex flex-col items-end justify-between text-right text-white">
          <div className="flex items-center gap-4">
            {/* Name / title on the left of the pet, right-aligned */}
            <div className="text-right">
              <div style={{ fontSize: '38px', lineHeight: 1.05 }} className="font-serif text-white">
                {fullName}
              </div>
              <div
                style={{ fontSize: '17px', marginTop: '6px' }}
                className="uppercase tracking-[0.25em] text-amber-300/90 font-medium"
              >
                {title}
              </div>
            </div>

            {/* Pet photo mirrors the logo on the left — matching size + ring.
                Defaults to Princess when the member hasn't picked their own. */}
            {hasPet && (
              <div
                className="rounded-full overflow-hidden bg-amber-50 ring-4 ring-amber-300/70 shrink-0"
                style={{ width: `${LOGO_PX}px`, height: `${LOGO_PX}px` }}
                data-testid="bc-pet-photo"
              >
                <img
                  src={effectivePetSrc}
                  alt={petName || 'My dog'}
                  crossOrigin="anonymous"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          <div className="flex items-end gap-6">
            <div className="text-right space-y-0.5" style={{ fontSize: '19px', lineHeight: 1.4 }}>
              <div className="text-white/95 select-all">{email}</div>
              {phone && <div className="text-white/95 select-all">{phone}</div>}
              <div className="text-amber-300/90 select-all">{website}</div>
              {hasSocials && (
                <div className="mt-1.5 space-y-0.5">
                  <SocialLine platform="x" handle={xHandle} />
                  <SocialLine platform="tiktok" handle={tiktokHandle} />
                </div>
              )}
            </div>
            <div
              className="bg-white p-2 rounded-md shrink-0"
              data-testid="business-card-qr"
            >
              <QRCodeSVG
                value={qrValue}
                size={120}
                bgColor="#ffffff"
                fgColor="#5c2a0d"
                level="M"
                includeMargin={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
